import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { GlassCard, LoadingSpinner } from './Layout';
import { TrendingUp, AlertTriangle, Info, Trophy, Coins, Clock, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => {
  if (n == null) return '-';
  const v = Math.round(n);
  return v < 0 ? `-$${Math.abs(v).toLocaleString()}` : `$${v.toLocaleString()}`;
};

// Screen hue, then the darker twin that survives white paper. Print CSS swaps --band
// for --band-print, so one set of markup serves both.
const BANDS = {
  strong: { hue: '#34d399', ink: '#047857', label: 'Strong investment' },
  solid: { hue: '#22d3ee', ink: '#0e7490', label: 'Solid investment' },
  thin: { hue: '#fbbf24', ink: '#b45309', label: 'Clears, but thinly' },
  risky: { hue: '#f87171', ink: '#b91c1c', label: "Doesn't justify the cost" },
  unknown: { hue: '#94a3b8', ink: '#C6D4E8', label: 'Not enough federal data' },
};

export const RoiBadge = ({ roi, className = '' }) => {
  if (!roi?.return_per_dollar) return null;
  const b = BANDS[roi.band] || BANDS.unknown;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}
      data-print-accent
      style={{ '--band': b.hue, '--band-print': b.ink,
               background: `${b.hue}1f`, border: '1px solid var(--band)', color: 'var(--band)' }}
      title={`${roi.verdict} Every $1 invested returns about $${roi.return_per_dollar} in extra lifetime earnings.`}
      data-testid={`roi-badge-${roi.id}`}>
      <TrendingUp className="w-3 h-3" />${roi.return_per_dollar} back per $1
    </span>
  );
};

const Row = ({ label, value, strong, hue }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
    <span className="text-xs text-slate-300">{label}</span>
    <span className={strong ? 'text-sm font-semibold' : 'text-sm text-slate-100'}
      style={strong ? { color: hue } : undefined}>{value}</span>
  </div>
);

const SchoolCard = ({ s, rank }) => {
  const b = BANDS[s.band] || BANDS.unknown;
  const [open, setOpen] = useState(rank === 0);

  return (
    <div className="relative rounded-2xl border p-5 overflow-hidden"
      data-print-accent
      style={{ '--band': b.hue, '--band-print': b.ink,
               borderColor: `${b.hue}44`,
               background: `linear-gradient(160deg, ${b.hue}10, rgba(255,255,255,0.02))` }}
      data-testid={`roi-school-${s.id}`}>
      <span aria-hidden className="absolute inset-x-0 top-0 h-[4px]" style={{ background: 'var(--band)' }} />
      {rank === 0 && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: `${b.hue}22`, color: 'var(--band)', border: '1px solid var(--band)' }}>
          <Trophy className="w-3 h-3" /> Best value
        </span>
      )}

      <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--band)' }}>{b.label}</p>
      <h3 className="text-lg font-semibold text-white mt-1 pr-24">{s.name}</h3>
      <p className="text-xs text-slate-300 mt-0.5">
        {s.major ? <>Priced on <span className="text-slate-200">{s.major.replace(/\.$/, '')}</span></>
          : 'Priced on the school-wide median'}
        {s.city ? ` · ${s.city}, ${s.state}` : ''}
      </p>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <div>
          <p className="text-2xl font-bold" style={{ color: 'var(--band)' }}>${s.return_per_dollar}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-300 mt-0.5">back per $1</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-white print:!text-sky-700">{money(s.net_gain)}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-300 mt-0.5">net lifetime gain</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-white print:!text-indigo-700">
            {s.payback_year ? `Yr ${s.payback_year}` : 'Never'}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-slate-300 mt-0.5">
            {s.years_after_graduation != null ? `${s.years_after_graduation} yrs after you finish` : 'no payback'}
          </p>
        </div>
      </div>

      <p className="text-sm text-slate-200/90 mt-4">{s.verdict}</p>

      {s.flags?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {s.flags.map((f, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs"
              style={{ color: f.level === 'bad' ? '#fca5a5' : f.level === 'warn' ? '#fcd34d' : '#94a3b8' }}>
              {f.level === 'info' ? <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
              {f.text}
            </p>
          ))}
        </div>
      )}

      <button onClick={() => setOpen(!open)} data-testid={`roi-breakdown-toggle-${s.id}`}
        className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors duration-200">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        {open ? 'Hide the maths' : 'Show me the maths'}
      </button>

      <div className={`mt-4 grid md:grid-cols-2 gap-x-6 ${open ? '' : 'hidden print:grid'}`}
        data-testid={`roi-breakdown-${s.id}`}>
          <div>
            <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-200/90 print:!text-amber-700 mb-1">
              <Coins className="w-3 h-3" /> What you put in
            </p>
            <Row label={`Net price × ${s.expected_years} years`} value={money(s.net_cost)} />
            <Row label="Pay you give up while studying" value={money(s.forgone_earnings)} />
            <Row label={`Interest on ${money(s.median_debt)} of debt`} value={money(s.loan_interest)} />
            <Row label="Total investment" value={money(s.total_investment)} strong hue="#b45309" />
            {s.monthly_payment > 0 && (
              <p className="text-[11px] text-slate-300 mt-1.5">
                ${s.monthly_payment}/month for ten years after you finish
                {s.debt_to_earnings ? ` · debt is ${s.debt_to_earnings}× your first-decade pay` : ''}
              </p>
            )}
          </div>
          <div className="mt-4 md:mt-0">
            <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-200/90 print:!text-emerald-700 mb-1">
              <TrendingUp className="w-3 h-3" /> What you get back
            </p>
            <Row label="Starting pay out of school" value={money(s.starting_pay)} />
            <Row label="Pay ten years in" value={money(s.earnings_at_10)} />
            <Row label="Lifetime earnings with this degree" value={money(s.lifetime_with_degree)} />
            <Row label="Lifetime without any degree" value={money(s.lifetime_without_degree)} />
            <Row label="Earnings advantage" value={money(s.earnings_advantage)} strong hue="#047857" />
          </div>

          {s.timeline?.length > 0 && (
            <div className="md:col-span-2 mt-4 overflow-x-auto">
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-300 mb-2">
                <Clock className="w-3 h-3" /> How it plays out
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-300 text-left">
                    <th className="pr-3 font-normal py-1">Year</th>
                    {s.timeline.map(t => <th key={t.year} className="pr-3 font-normal py-1">{t.year}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[['This route', 'with_degree', '#059669'],
                    ['No degree', 'without_degree', '#C6D4E8'],
                    ['Ahead by', 'cumulative_advantage', '#0891b2']].map(([label, key, hue]) => (
                    <tr key={key} className="border-t border-white/5">
                      <td className="pr-3 py-1.5 text-slate-300">{label}</td>
                      {s.timeline.map(t => (
                        <td key={t.year} className="pr-3 py-1.5" style={{ color: hue }}>
                          {money(t[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

/** Education investment for the person's saved list, or any set of schools passed in. */
export const EducationInvestment = ({ schools: input }) => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssumptions, setShowAssumptions] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = input?.length
        ? await axios.post(`${API}/education-roi/schools`, { schools: input }, getAuthHeader())
        : await axios.get(`${API}/education-roi/my-schools`, getAuthHeader());
      setD(res.data);
    } catch { setD(null); }
    finally { setLoading(false); }
  }, [getAuthHeader, input]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  if (!d?.schools?.length) {
    return (
      <GlassCard className="mt-4" data-testid="roi-empty">
        <p className="text-sm text-slate-300">
          Save a college to your list and we'll price it, what it costs you all in, what it pays back,
          and how long that takes.
        </p>
      </GlassCard>
    );
  }

  return (
    <div className="mt-4 space-y-4" data-testid="education-investment">
      <GlassCard>
        <p className="text-[11px] uppercase tracking-wider text-cyan-300">Education investment</p>
        <h2 className="font-outfit text-xl md:text-2xl font-semibold text-white mt-1">
          {d.comparison ? d.comparison.headline
            : `What ${d.schools[0].name} actually returns.`}
        </h2>
        {d.intended_major && (
          <p className="text-sm text-slate-300/85 mt-2">
            Priced on <span className="text-white font-medium">{d.intended_major}</span> wherever the
            school reports it, the school-wide average blends every major and everyone who left early.
          </p>
        )}
        {d.comparison?.notes?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {d.comparison.notes.map((n, i) => (
              <li key={i} className="text-sm text-slate-300/85 flex gap-2">
                <span className="text-cyan-300">·</span>{n}
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      <div className="grid gap-4">
        {d.schools.map((s, i) => <SchoolCard key={s.id || s.name} s={s} rank={i} />)}
      </div>

      {d.unusable?.length > 0 && (
        <p className="text-xs text-slate-300">
          No federal earnings or price reported for: {d.unusable.join(', ')}. We won't guess.
        </p>
      )}

      <GlassCard>
        <button onClick={() => setShowAssumptions(!showAssumptions)} data-testid="roi-assumptions-toggle"
          className="flex items-center gap-2 text-sm text-slate-200 hover:text-white transition-colors duration-200">
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showAssumptions ? 'rotate-180' : ''}`} />
          How we work this out, every assumption, named
        </button>
        <ul className={`mt-3 space-y-2 ${showAssumptions ? '' : 'hidden print:block'}`}
          data-testid="roi-assumptions">
            {d.assumptions.map((a, i) => (
              <li key={i} className="text-xs text-slate-300 flex gap-2"><span className="text-slate-300">{i + 1}.</span>{a}</li>
            ))}
        </ul>
      </GlassCard>
    </div>
  );
};

export default EducationInvestment;
